import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import BarcodeScanner from '../../components/BarcodeScanner';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import './ScanAndGo.css';

interface Product {
  id: number;
  name: string;
  brand: string;
  category: string;
  price: string;
  size_lbs?: string;
  upc?: string;
  sku?: string;
  is_byoc: boolean;
}

interface SessionItem {
  id: number;
  product: Product;
  quantity: number;
  weight_lbs?: string;
  is_weighted: boolean;
  line_total: string;
}

interface ScanSession {
  session_id: string;
  status: string;
  items: SessionItem[];
  subtotal: string;
  tax: string;
  total: string;
  item_count: number;
}

const ScanAndGo: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();
  const { member } = useBridgeClubAuth();
  const [session, setSession] = useState<ScanSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [lastScannedProduct, setLastScannedProduct] = useState<Product | null>(null);
  const [error, setError] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [showWeightInput, setShowWeightInput] = useState(false);
  const [pendingByocProduct, setPendingByocProduct] = useState<Product | null>(null);
  const [byocWeight, setByocWeight] = useState('');
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  // The page told customers to "use your phone camera" while containing no
  // camera at all - it was a focused text input expecting a hardware wedge
  // scanner. On a phone that instruction was simply false.
  const [showCamera, setShowCamera] = useState(false);

  // Product search state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }

    // Check if first time using Scan & Go
    if (member && !member.scan_and_go_tutorial_complete) {
      setShowTutorial(true);
    }

    loadSession();
  }, [hasLocalAccess, navigate, member]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getScanAndGoSession();
      setSession(data);
    } catch (err) {
      console.error('Failed to load session:', err);
      setError('Failed to load shopping session');
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitBarcode(barcodeInput);
  };

  // A camera scan takes exactly the path a typed code does. Kept as one
  // function so the two can never drift into behaving differently.
  const handleCameraClose = useCallback(() => setShowCamera(false), []);

  const handleCameraScan = useCallback((code: string) => {
    setShowCamera(false);
    setBarcodeInput(code);
    submitBarcode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitBarcode = async (raw: string) => {
    const barcodeInput = (raw || '').trim();
    if (!barcodeInput) return;

    setScanning(true);
    setError('');
    setLastScannedProduct(null);

    try {
      // Check if it's a BYOC bin QR code
      if (barcodeInput.startsWith('BYOC-') || barcodeInput.startsWith('BIN-')) {
        const result = await bridgeClubAPI.scanAndGoLookupBYOCBin(barcodeInput);
        if (result.product) {
          // BYOC items need weight input
          setPendingByocProduct(result.product);
          setShowWeightInput(true);
          setLastScannedProduct(result.product);
        } else {
          setError('BYOC bin not found or out of stock');
        }
      } else {
        // Regular barcode lookup
        const result = await bridgeClubAPI.scanAndGoLookupBarcode(barcodeInput);
        if (result.product) {
          // Add to session
          await bridgeClubAPI.addToScanAndGoSession(result.product.id, 1, false);
          setLastScannedProduct(result.product);
          await loadSession();
        } else {
          setError('Product not found');
        }
      }
    } catch (err: any) {
      console.error('Scan failed:', err);
      setError(err.message || 'Failed to scan product');
    } finally {
      setScanning(false);
      setBarcodeInput('');
      barcodeInputRef.current?.focus();
    }
  };

  const handleByocWeightSubmit = async () => {
    if (!pendingByocProduct || !byocWeight) return;

    const weight = parseFloat(byocWeight);
    if (isNaN(weight) || weight <= 0) {
      setError('Please enter a valid weight');
      return;
    }

    try {
      await bridgeClubAPI.addToScanAndGoSession(pendingByocProduct.id, 1, true, weight);
      await loadSession();
      setShowWeightInput(false);
      setPendingByocProduct(null);
      setByocWeight('');
    } catch (err: any) {
      setError(err.message || 'Failed to add BYOC item');
    }
  };

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) {
      await handleRemoveItem(itemId);
      return;
    }

    try {
      await bridgeClubAPI.updateScanAndGoItem(itemId, newQuantity);
      await loadSession();
    } catch (err: any) {
      setError(err.message || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    try {
      await bridgeClubAPI.removeScanAndGoItem(itemId);
      await loadSession();
    } catch (err: any) {
      setError(err.message || 'Failed to remove item');
    }
  };

  const handleCheckout = () => {
    if (!session || session.items.length === 0) return;
    navigate('/bridge-club/scan-and-go/checkout');
  };

  const completeTutorial = async () => {
    try {
      await bridgeClubAPI.completeScanAndGoTutorial();
      setShowTutorial(false);
    } catch (err) {
      console.error('Failed to complete tutorial:', err);
      setShowTutorial(false);
    }
  };

  const focusBarcodeInput = useCallback(() => {
    barcodeInputRef.current?.focus();
  }, []);

  // Auto-focus barcode input when returning to page
  useEffect(() => {
    if (!showTutorial && !showWeightInput && !showSearchModal) {
      focusBarcodeInput();
    }
  }, [showTutorial, showWeightInput, showSearchModal, focusBarcodeInput]);

  // Product Search Functions
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setSelectedProduct(null);

    // Clear previous debounce timer
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    // Debounce search
    searchDebounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const products = await bridgeClubAPI.getLocalProducts(undefined, query.trim());
        setSearchResults(products || []);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setAddQuantity(1);
  };

  const handleAddSearchedProduct = async () => {
    if (!selectedProduct) return;

    try {
      if (selectedProduct.is_byoc) {
        // BYOC items need weight input
        setPendingByocProduct(selectedProduct);
        setShowSearchModal(false);
        setShowWeightInput(true);
        resetSearch();
      } else {
        // Regular product - add to cart
        await bridgeClubAPI.addToScanAndGoSession(selectedProduct.id, addQuantity, false);
        setLastScannedProduct(selectedProduct);
        await loadSession();
        setShowSearchModal(false);
        resetSearch();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add product');
    }
  };

  const resetSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedProduct(null);
    setAddQuantity(1);
  };

  const openSearchModal = () => {
    setShowSearchModal(true);
    resetSearch();
    // Focus search input after modal opens
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const closeSearchModal = () => {
    setShowSearchModal(false);
    resetSearch();
  };

  if (loading) {
    return (
      <BridgeClubLayout>
        <div className="scan-and-go-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  // First-time tutorial
  if (showTutorial) {
    return (
      <BridgeClubLayout>
        <div className="scan-tutorial">
          <div className="tutorial-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Scan Products</h3>
              <p>Use your phone camera or a scanner to scan product barcodes as you shop.</p>
            </div>
          </div>

          <div className="tutorial-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>BYOC Bulk Items</h3>
              <p>For bulk items, scan the bin QR code, then weigh your container and enter the weight.</p>
            </div>
          </div>

          <div className="tutorial-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Pay & Go</h3>
              <p>When done shopping, tap checkout to pay with your saved card or add a new one.</p>
            </div>
          </div>

          <div className="tutorial-step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Show Receipt</h3>
              <p>Show your digital receipt to staff at the exit for quick verification.</p>
            </div>
          </div>

          <button className="tutorial-start-btn" onClick={completeTutorial}>
            Start Shopping
          </button>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      {showCamera && (
        <BarcodeScanner
          onScan={handleCameraScan}
          onClose={handleCameraClose}
          continuousMode={false}
        />
      )}
      <div className="scan-and-go">
        {/* Scanner Input */}
        <div className="scanner-section">
          <form onSubmit={handleBarcodeSubmit} className="scanner-form">
            <input
              ref={barcodeInputRef}
              type="text"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              placeholder="Scan barcode or enter UPC..."
              className="barcode-input"
              autoFocus
              disabled={scanning}
            />
            <button type="submit" className="scan-btn" disabled={scanning || !barcodeInput.trim()}>
              {scanning ? 'Scanning...' : 'Add'}
            </button>
          </form>

          <button
            type="button"
            className="search-products-btn"
            onClick={() => setShowCamera(true)}
          >
            Scan with camera
          </button>

          <button className="search-products-btn" onClick={openSearchModal}>
            Search Products
          </button>

          {error && <div className="scan-error">{error}</div>}

          {lastScannedProduct && !error && (
            <div className="last-scanned">
              Added: {lastScannedProduct.name}
            </div>
          )}
        </div>

        {/* Product Search Modal */}
        {showSearchModal && (
          <div className="search-modal-overlay" onClick={closeSearchModal}>
            <div className="search-modal" onClick={(e) => e.stopPropagation()}>
              <div className="search-modal-header">
                <h3>Search Products</h3>
                <button className="close-modal-btn" onClick={closeSearchModal}>&times;</button>
              </div>

              <div className="search-input-wrapper">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Type product name..."
                  className="search-input"
                />
                {searchLoading && <div className="search-spinner"></div>}
              </div>

              <div className="search-results">
                {searchQuery.length < 2 ? (
                  <p className="search-hint">Type at least 2 characters to search</p>
                ) : searchLoading ? (
                  <p className="search-hint">Searching...</p>
                ) : searchResults.length === 0 ? (
                  <p className="search-hint">No products found</p>
                ) : (
                  searchResults.map((product) => (
                    <div
                      key={product.id}
                      className={`search-result-item ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="result-info">
                        <span className="result-name">{product.name}</span>
                        {product.brand && <span className="result-brand">{product.brand}</span>}
                        {product.size_lbs && <span className="result-size">{product.size_lbs} lbs</span>}
                      </div>
                      <span className="result-price">
                        ${parseFloat(product.price).toFixed(2)}
                        {product.is_byoc && '/lb'}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {selectedProduct && (
                <div className="search-add-section">
                  <div className="selected-product-info">
                    <span className="selected-name">{selectedProduct.name}</span>
                    <span className="selected-price">
                      ${parseFloat(selectedProduct.price).toFixed(2)}
                      {selectedProduct.is_byoc && '/lb'}
                    </span>
                  </div>

                  {!selectedProduct.is_byoc && (
                    <div className="quantity-selector">
                      <span>Quantity:</span>
                      <div className="qty-controls">
                        <button
                          className="qty-btn"
                          onClick={() => setAddQuantity(Math.max(1, addQuantity - 1))}
                        >
                          -
                        </button>
                        <span className="qty-value">{addQuantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => setAddQuantity(addQuantity + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  <button className="add-to-cart-btn" onClick={handleAddSearchedProduct}>
                    {selectedProduct.is_byoc ? 'Enter Weight' : `Add ${addQuantity} to Cart`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BYOC Weight Input Modal */}
        {showWeightInput && pendingByocProduct && (
          <div className="weight-modal-overlay" onClick={() => setShowWeightInput(false)}>
            <div className="weight-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Enter Weight</h3>
              <p className="weight-product-name">{pendingByocProduct.name}</p>
              <p className="weight-price">
                ${parseFloat(pendingByocProduct.price).toFixed(2)}/lb
              </p>
              <div className="weight-input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={byocWeight}
                  onChange={(e) => setByocWeight(e.target.value)}
                  placeholder="0.00"
                  className="weight-input"
                  autoFocus
                />
                <span className="weight-unit">lbs</span>
              </div>
              {byocWeight && !isNaN(parseFloat(byocWeight)) && (
                <p className="weight-total">
                  Total: ${(parseFloat(pendingByocProduct.price) * parseFloat(byocWeight)).toFixed(2)}
                </p>
              )}
              <div className="weight-actions">
                <button className="cancel-btn" onClick={() => {
                  setShowWeightInput(false);
                  setPendingByocProduct(null);
                  setByocWeight('');
                }}>
                  Cancel
                </button>
                <button className="confirm-btn" onClick={handleByocWeightSubmit}>
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cart Items */}
        <div className="cart-section">
          <h3 className="cart-header">
            Your Items ({session?.item_count || 0})
          </h3>

          {session && session.items.length > 0 ? (
            <div className="cart-items">
              {session.items.map((item) => (
                <div key={item.id} className="cart-item">
                  <div className="item-details">
                    <span className="item-name">{item.product.name}</span>
                    {item.is_weighted && item.weight_lbs && (
                      <span className="item-weight">{item.weight_lbs} lbs</span>
                    )}
                    <span className="item-price">${parseFloat(item.line_total).toFixed(2)}</span>
                  </div>
                  <div className="item-actions">
                    {!item.is_weighted && (
                      <div className="quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                        >
                          -
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                    )}
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveItem(item.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-cart">
              <p>Scan items to add them to your cart</p>
            </div>
          )}
        </div>

        {/* Checkout Footer */}
        {session && session.items.length > 0 && (
          <div className="checkout-footer">
            <div className="totals">
              <div className="subtotal-row">
                <span>Subtotal</span>
                <span>${parseFloat(session.subtotal).toFixed(2)}</span>
              </div>
              <div className="tax-row">
                <span>Tax (8.0%)</span>
                <span>${parseFloat(session.tax).toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Total</span>
                <span>${parseFloat(session.total).toFixed(2)}</span>
              </div>
            </div>
            <button className="checkout-btn" onClick={handleCheckout}>
              Pay ${parseFloat(session.total).toFixed(2)}
            </button>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default ScanAndGo;
